import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeConfig, themes, defaultTheme, applyTheme } from '../config/theme';

type LayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';

// Sidebar width constraints
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 400;
const SIDEBAR_DEFAULT_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 80;

interface ThemeState {
  currentTheme: string;
  isDarkMode: boolean;
  sidebarCollapsed: boolean;
  sidebarWidth: number; // Dynamic sidebar width
  layoutType: LayoutType;
  config: ThemeConfig;
  isSuperAdminMode: boolean; // Flag para indicar se o usuário é Super Admin
  
  // Actions
  setTheme: (themeName: string) => void;
  toggleDarkMode: () => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setCustomTheme: (config: ThemeConfig) => void;
  setLayoutType: (layoutType: LayoutType) => void;
  setSuperAdminMode: (isSuperAdmin: boolean) => void;
}

export { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_DEFAULT_WIDTH, SIDEBAR_COLLAPSED_WIDTH };

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      currentTheme: 'cyber-cyan',
      isDarkMode: true,
      sidebarCollapsed: false,
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      layoutType: 'SIDEBAR',
      config: defaultTheme,
      isSuperAdminMode: false,

      setTheme: (themeName: string) => {
        const theme = themes[themeName] || defaultTheme;
        applyTheme(theme);
        set({ currentTheme: themeName, config: theme });
      },

      toggleDarkMode: () => {
        const { isDarkMode } = get();
        document.documentElement.classList.toggle('light', isDarkMode);
        set({ isDarkMode: !isDarkMode });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarWidth: (width: number) => {
        // Clamp width between min and max
        const clampedWidth = Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH);
        set({ sidebarWidth: clampedWidth });
      },

      setCustomTheme: (config: ThemeConfig) => {
        const { isSuperAdminMode } = get();
        
        // Super Admin não deve ter temas customizados aplicados
        // Isso garante isolamento total da interface do Super Admin
        if (isSuperAdminMode) {
          console.log('[ThemeStore] Blocking custom theme for Super Admin - using default theme');
          return;
        }
        
        applyTheme(config);
        set({ config, currentTheme: 'custom' });
      },

      setLayoutType: (layoutType: LayoutType) => {
        const { isSuperAdminMode } = get();
        
        // Super Admin sempre usa layout SIDEBAR
        if (isSuperAdminMode && layoutType !== 'SIDEBAR') {
          console.log('[ThemeStore] Blocking layout change for Super Admin - keeping SIDEBAR');
          return;
        }
        
        set({ layoutType });
      },

      setSuperAdminMode: (isSuperAdmin: boolean) => {
        console.log('[ThemeStore] Setting Super Admin mode:', isSuperAdmin);
        set({ isSuperAdminMode: isSuperAdmin });
        
        // Se é Super Admin, garantir que o tema padrão seja aplicado
        if (isSuperAdmin) {
          const theme = themes['cyber-cyan'] || defaultTheme;
          applyTheme(theme);
          set({ 
            currentTheme: 'cyber-cyan', 
            config: theme,
            layoutType: 'SIDEBAR',
          });
        }
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        console.log('[ThemeStore] Rehydrating from localStorage:', state?.currentTheme);
        if (state) {
          // Só aplica o tema do localStorage se for um tema customizado
          // O useTenantTheme vai aplicar o tema correto do backend
          if (state.currentTheme === 'custom') {
            console.log('[ThemeStore] Skipping rehydration - custom theme will be loaded from backend');
          } else {
            console.log('[ThemeStore] Applying rehydrated theme:', state.config?.name);
            applyTheme(state.config);
          }
          if (!state.isDarkMode) {
            document.documentElement.classList.add('light');
          }
        }
      },
    }
  )
);
